import React, { FC, ReactElement } from 'react'
import PlusIcon from '@/icons/plus-circle'
import MinusIcon from '@/icons/minus-circle'
import RuneCard from './rune-transfer-card'
import formatNumber from '@/utils/format-number'
import Input from '@/components/input'
import { API_URL, NETWORK } from '@/utils/constants'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import FeeSlider from '@/components/fee-slider'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Button from '@/components/button'
import useRuneBalance from '@/utils/hooks/useRuneBalance'
import useRuneOutpoints from '@/utils/hooks/useRuneOutpoints'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import event from '@/utils/event'
import useDebounce from '@/utils/hooks/useDebounce'
import Link from 'next/link'

const BRC20Transfer: FC<{
	ticker: string
	balance: any
	onComplete: () => void
}> = (props): ReactElement => {
	const { ticker, balance, onComplete } = props
	const [numTransfers, setTransfers] = React.useState(1)
	const [feeRate, setFeeRate] = React.useState(10.0)
	const [fee, setFee] = React.useState(0)
	const [psbt, setPsbt] = React.useState<any>(null)
	const [error, setError] = React.useState<any>(null)
	const loadingFeeRef = React.useRef(false)
	const [loading, setLoading] = React.useState(false)
	const [amounts, setAmounts] = React.useState({})
	const [addresses, setAddresses] = React.useState({})
	const { exchangeRate } = useExchangeRate()
	const feePrice = useSatoshiPrice(fee)

	const { data: runeData } = useRuneBalance()

	const newRunes = React.useMemo(() => new Array(numTransfers).fill(''), [numTransfers])

	const totalAmount = React.useMemo(
		() =>
			newRunes
				.map((e, i) => ((amounts as any)[i] ? parseInt((amounts as any)[i], 10) : 0))
				.reduce((a, e) => a + e, 0),
		[amounts, newRunes]
	)

	const selectedRune = React.useMemo(() => {
		const selectedRune = runeData.find((e) => e.ticker === ticker)
		//const res = (runeOutpoints || [])
		//.filter((e) => e.rune_id == selectedRune?.rune_id)
		//.sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
		return selectedRune
	}, [runeData, ticker])

	const { data: runeOutpoints } = useRuneOutpoints(selectedRune?.rune_id)
	const runes = React.useMemo(() => runeOutpoints || [], [runeOutpoints])
	const extractUtxo = runes?.find((e) => e.sats > 10000)

	const currentAvailable = React.useMemo(() => {
		if (extractUtxo) {
			return parseFloat(extractUtxo.amount) - totalAmount
		}

		return parseFloat(balance.overall_balance) - totalAmount
	}, [totalAmount, balance, extractUtxo])

	const handleSubmit = React.useCallback(async () => {
		setLoading(true)

		try {
			const rawtx = await Wallet.signPSBTExtractTx(psbt as any)
			const { data } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx: rawtx })

			if (data.error) {
				setError(data.error.message)
			} else {
				event.emit('open-broadcast-modal', { event: 'success', txid: data.result })
				onComplete()
			}
		} catch (e) {
			setError((e as any).message || (e as any).toString())
		}

		setLoading(false)
	}, [psbt, onComplete])

	const renderTransfer = React.useCallback(
		(e: any, i: any) => {
			return (
				<RuneCard
					key={i}
					ticker={ticker}
					description={`Available: ${formatNumber(`${Math.max(currentAvailable, 0)}`)}`}
					collection={selectedRune.collection}
					onRemove={() => {}}
				>
					<Input
						placeholder="Amount"
						className="border border-border rounded-full h-10"
						inputClassName="text-sm rounded-full"
						type="number"
						onChange={(evt) => {
							setAmounts((prev) => ({ ...prev, [i]: evt.target.value }))
						}}
						value={(amounts as any)[i] || ''}
					/>
					<Input
						placeholder="Address (empty to send to self)"
						className="border border-border rounded-full h-10"
						inputClassName="text-sm rounded-full"
						onChange={(evt) => {
							setAddresses((prev) => ({ ...prev, [i]: evt.target.value }))
						}}
						value={(addresses as any)[i] || ''}
					/>
				</RuneCard>
			)
		},
		[ticker, currentAvailable, selectedRune.collection, amounts, addresses]
	)

	const dFeeRate: number = useDebounce<number>(feeRate, 300)
	const dAmounts = useDebounce(amounts, 300)
	const dAddresses = useDebounce(addresses, 300)

	React.useEffect(() => {
		async function run() {
			loadingFeeRef.current = true
			setError(true as any)
			setPsbt(null)
			setFee(0)

			if (
				!Object.keys(dAmounts).find((e) => !(dAmounts as any)[e]) &&
				Object.keys(dAmounts).length
			) {
				try {
					const edicts = newRunes.map((e, i) => [
						selectedRune.rune_id,
						parseFloat((dAmounts as any)[i]),
						selectedRune.divisibility,
						(dAddresses as any)[i] || Wallet.getTaprootAddress()
					])

					const outpoints = []

					if (extractUtxo) {
						outpoints.push([extractUtxo.outpoint, extractUtxo.sats])
					} else {
						let count = 0
						for (let rune of runes) {
							if (count > totalAmount) {
								break
							}

							count = count + parseFloat(rune.amount)
							outpoints.push([rune.outpoint, rune.sats])
						}
					}

					const { psbt, fee } = await Wallet.getRuneTransfer(edicts, outpoints, dFeeRate)

					setFee(Number(fee))
					setPsbt(psbt as any)
				} catch (e) {
					setError((e as any)?.response?.data?.message || (e as any).message || e)
				}
			}

			loadingFeeRef.current = false
		}

		run()
	}, [newRunes, selectedRune, dAmounts, dAddresses, dFeeRate, extractUtxo, runes, totalAmount])

	const disabled = React.useMemo(
		() =>
			loading ||
			!!Object.keys(amounts).find((e) => !(amounts as any)[e]) ||
			!Object.keys(amounts).length ||
			!psbt,
		[loading, amounts, psbt]
	)

	const extractPrice = useSatoshiPrice(extractUtxo?.sats)

	return (
		<div>
			<div className="flex justify-between">
				<p className="text-base font-bold">Transfers ({numTransfers})</p>
				<div className="flex gap-4">
					<MinusIcon
						className="size-[20px] stroke-[#B692F6]"
						onClick={() => setTransfers(Math.max(1, numTransfers - 1))}
					/>
					<PlusIcon
						className="size-[20px] stroke-[#B692F6]"
						onClick={() => setTransfers(numTransfers + 1)}
					/>
				</div>
			</div>
			{!!extractUtxo && (
				<p className="text-xs text-yellow-500">
					You have {extractUtxo.amount} runes on a utxo with {extractPrice} {NETWORK}! You must send
					them to yourself to extact thse sats.
				</p>
			)}
			<div className="bg-card p-4 flex flex-col gap-4 rounded-xl border border-border mt-4">
				{newRunes.map(renderTransfer)}
			</div>
			<div className="border border-border rounded-xl p-3 mt-4">
				<FeeSlider feeRate={feeRate} setFeeRate={setFeeRate} />
				<div className="flex items-center justify-between mt-4">
					<p className="text-sm font-bold text-foreground/80">Total fee cost</p>
					<div className="flex flex-col justify-right items-right">
						<p className="text-xs font-bold text-right">
							{feePrice} {NETWORK}
						</p>
						<p className="text-sm font-regular text-muted-foreground text-right">
							${((fee / 1e8) * exchangeRate).toFixed(2)}
						</p>
					</div>
				</div>
			</div>
			{error && <p className="mt-4 text-red-500 text-xs">{error}</p>}
			<div className="items-center text-muted-foreground text-xs mt-4">
				By clicking "send now", you agree to the Ordinals Wallet{' '}
				<Link href={`/terms`} className="underline" target="_blank">
					Terms of Service.
				</Link>
			</div>
			<div className="mt-6">
				<ButtonGradientEpic
					disabled={disabled}
					onClick={handleSubmit}
					loading={loading}
					className="h-[48px] rounded-full w-full"
				>
					Send Now
				</ButtonGradientEpic>
				<Button
					className="text-muted-foreground rounded-full text-base font-semibold p-0 h-12"
					onClick={onComplete}
				>
					Close
				</Button>
			</div>
		</div>
	)
}

export default BRC20Transfer
